import React from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { getAuth, confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { useNavigate, useSearchParams } from 'react-router-dom';

const ResetPassword = () => {
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [verifying, setVerifying] = React.useState(true);
  const [message, setMessage] = React.useState({ type: '', text: '' });
  const [oobCode, setOobCode] = React.useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  React.useEffect(() => {
    const code = searchParams.get('oobCode');
    if (!code) {
      setMessage({ type: 'error', text: 'Link inválido ou expirado.' });
      setVerifying(false);
      return;
    }

    setOobCode(code);
    
    // Verificar se o código é válido
    const auth = getAuth();
    verifyPasswordResetCode(auth, code)
      .then(() => {
        setVerifying(false);
      })
      .catch((error) => {
        console.error('Erro ao verificar código:', error);
        let errorMessage = 'Link inválido ou expirado.';
        
        switch (error.code) {
          case 'auth/invalid-action-code':
            errorMessage = 'Link inválido ou já utilizado.';
            break;
          case 'auth/expired-action-code':
            errorMessage = 'Link expirado. Solicite um novo link de recuperação.';
            break;
          case 'auth/invalid-email':
            errorMessage = 'Email inválido.';
            break;
          default:
            errorMessage = 'Link inválido ou expirado.';
        }
        
        setMessage({ type: 'error', text: errorMessage });
        setVerifying(false);
      });
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password || !password.trim()) {
      setMessage({ type: 'error', text: 'Por favor, informe uma nova senha.' });
      return;
    }

    if (password.length < 6) {
      setMessage({ type: 'error', text: 'A senha deve ter pelo menos 6 caracteres.' });
      return;
    }

    if (password !== confirmPassword) {
      setMessage({ type: 'error', text: 'As senhas não coincidem.' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const auth = getAuth();
      await confirmPasswordReset(auth, oobCode, password);
      
      setMessage({
        type: 'success',
        text: 'Senha alterada com sucesso! Você será redirecionado para o login.'
      });
      
      // Redirecionar para login após 3 segundos
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 3000);
      
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      
      let errorMessage = 'Erro ao alterar senha. Tente novamente.';
      
      switch (error.code) {
        case 'auth/invalid-action-code':
          errorMessage = 'Link inválido ou já utilizado.';
          break;
        case 'auth/expired-action-code':
          errorMessage = 'Link expirado. Solicite um novo link de recuperação.';
          break;
        case 'auth/weak-password':
          errorMessage = 'Senha muito fraca. Escolha uma senha mais segura.';
          break;
        default:
          errorMessage = 'Erro ao alterar senha. Tente novamente.';
      }
      
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/', { replace: true });
  };

  if (verifying) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          p: 2,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            maxWidth: 400,
            width: '100%',
            textAlign: 'center',
          }}
        >
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="h6">
            Verificando link...
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 400,
          width: '100%',
          textAlign: 'center',
        }}
      >
        <Typography variant="h5" component="h1" gutterBottom>
          Nova Senha
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Digite sua nova senha abaixo.
        </Typography>

        {message.text && (
          <Alert 
            severity={message.type === 'error' ? 'error' : 'success'} 
            sx={{ mb: 3 }}
          >
            {message.text}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Nova Senha"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            sx={{ mb: 2 }}
            autoComplete="new-password"
            autoFocus
            helperText="Mínimo 6 caracteres"
          />
          
          <TextField
            fullWidth
            label="Confirmar Nova Senha"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading}
            sx={{ mb: 3 }}
            autoComplete="new-password"
          />
          
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{ mb: 2 }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Alterar Senha'
            )}
          </Button>
        </Box>

        <Link
          component="button"
          variant="body2"
          onClick={handleBackToLogin}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            textDecoration: 'none',
            color: 'primary.main',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          <ArrowBackIcon fontSize="small" />
          Voltar para o Login
        </Link>
      </Paper>
    </Box>
  );
};

export default ResetPassword;
